/**
 * B+D visual redesign — Authenticated Home.
 *
 * The Home surface is the Chat empty state for authenticated players.
 * Three states, determined by props (logic preserved from XX.285h):
 *
 *   State 1 — active mission: greeting, remembered moment, "You're working
 *             on …" + "I tried it" (advances the development loop).
 *   State 2 — memory, no mission: greeting, remembered moment, invitation
 *             to continue. No mission placeholder — never manufacture
 *             structure to fill the screen.
 *   State 3 — no memory: clean greeting + invitation. No fake memory.
 *
 * Design: the information is the interface. System font throughout.
 * No cards, no gradients, no character art, no starter grid, no badges.
 * One quiet greeting, the remembered text, one primary action ("I tried
 * it" for State 1), and the composer prompt. Generous whitespace.
 * The chat input must remain visible without scrolling on normal mobile
 * viewports — long remembered text truncates with expansion.
 *
 * Props (interface preserved for Chat.jsx compatibility):
 *   user, onPrimaryTap, onVideoTap, showBackToConversation,
 *   onBackToConversation, disabled, activeMission, rememberedMoment,
 *   onMissionTriedTap, sessionId — used.
 *   onStarterTap, continueHint, onContinueTap, narrativeEnabled,
 *   emptyState, journeySignalsCount, challengeBanner — accepted but
 *   not rendered in this design (simplified per B+D brief).
 */

import React from "react";
import RememberedMoment from "./RememberedMoment";
import { hapticLight } from "../lib/haptics";

function timeOfDayGreeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function pickFirstName(user) {
  if (!user || typeof user !== "object") return null;
  const raw =
    user.first_name ||
    user.firstName ||
    user.display_name ||
    user.name ||
    "";
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0].slice(0, 20);
}

function missionFocusText(mission) {
  if (!mission || typeof mission !== "object") return null;
  return (
    mission.focus_text ||
    mission.focus ||
    mission.title ||
    mission.mission_text ||
    null
  );
}

export default function AuthedHomeHero({
  user,
  onPrimaryTap,
  onVideoTap,
  showBackToConversation = false,
  onBackToConversation,
  disabled = false,
  activeMission = null,
  rememberedMoment = null,
  onMissionTriedTap,
  sessionId,
  // Accepted for compatibility; not rendered in this design.
  onStarterTap,
  continueHint,
  onContinueTap,
  narrativeEnabled,
  emptyState,
  journeySignalsCount,
  challengeBanner,
}) {
  const firstName = pickFirstName(user);
  const greeting = firstName
    ? `${timeOfDayGreeting()}, ${firstName}.`
    : `${timeOfDayGreeting()}.`;
  const focusText = missionFocusText(activeMission);
  const hasMission = Boolean(activeMission && focusText);
  const hasRemembered = Boolean(rememberedMoment && rememberedMoment.display_text);

  const handleTriedIt = () => {
    if (disabled) return;
    hapticLight();
    try {
      if (onMissionTriedTap && activeMission) {
        onMissionTriedTap(activeMission.mission_id || activeMission.id, focusText);
      }
    } catch { /* analytics best-effort */ }
  };

  return (
    <div
      className="px-6 pt-10 pb-6 max-w-lg mx-auto"
      data-testid="authed-home-hero"
    >
      {showBackToConversation && onBackToConversation && (
        <button
          onClick={onBackToConversation}
          className="text-sm text-neutral-400 mb-8 underline underline-offset-2 decoration-neutral-200"
          data-testid="back-to-conversation"
        >
          Back to conversation
        </button>
      )}

      {/* Greeting — large, light, quiet. */}
      <h1 className="text-[28px] font-light text-neutral-900 tracking-tight">
        {greeting}
      </h1>

      {/* State 1+2: remembered moment. Renders nothing when null. */}
      {hasRemembered && (
        <div className="mt-8">
          <RememberedMoment moment={rememberedMoment} />
        </div>
      )}

      {/* State 1: active mission — "You're working on …" + "I tried it". */}
      {hasMission && (
        <div className="mt-6">
          <p className="text-[15px] text-neutral-600 leading-relaxed">
            You&rsquo;re working on {focusText}.
          </p>
          <button
            onClick={handleTriedIt}
            disabled={disabled}
            className="mt-2 text-[15px] font-medium text-emerald-700 underline underline-offset-2 decoration-emerald-300 disabled:opacity-40"
            data-testid="mission-tried-it"
          >
            I tried it
          </button>
        </div>
      )}

      {/* State 2: memory but no mission — quiet invitation, no placeholder. */}
      {!hasMission && hasRemembered && (
        <div className="mt-6">
          <p className="text-[15px] text-neutral-600 leading-relaxed">
            Pick up where you left off, or start something new.
          </p>
        </div>
      )}

      {/* State 3: no memory — clean invitation, no fake memory. */}
      {!hasMission && !hasRemembered && (
        <div className="mt-6">
          <p className="text-[15px] text-neutral-600 leading-relaxed">
            What are you working on?
          </p>
        </div>
      )}

      {/* Video — quiet secondary action near the composer. */}
      <div className="mt-6 text-center">
        <button
          onClick={onVideoTap}
          disabled={disabled}
          className="text-sm text-neutral-400 underline underline-offset-2 decoration-neutral-200 disabled:opacity-40"
          data-testid="home-video-link"
        >
          Show me a video
        </button>
      </div>
    </div>
  );
}
